"use client";

import React, { useMemo, useState } from "react";

import { ProfileFilterChipGroup } from "./ProfileFilterChipGroup";
import { ServicesPricingOfferingIcon } from "./ServicesPricingOfferingIcon";
import { ServicesPricingSkillIcon } from "./ServicesPricingSkillIcon";
import {
  getServicesPricingOfferingIconPresentation,
} from "./profileServicesPricingOfferingIcons";
import {
  buildServicesPricingConditionDisplays,
  buildServicesPricingOfferingDisplays,
  buildServicesPricingSkillDisplays,
  collectOfferingIndustryFilters,
  filterOfferingsByIndustry,
  type ServiceConditionInput,
  type ServiceOfferingInput,
  type SkillInput,
} from "./profileServicesPricingDisplay";

export type ProfileServicesPricingCapabilityProjection = Readonly<{
  skills: readonly Readonly<{
    publicSkillKey: string;
    name: string;
  }>[];
}>;

export type ProfileServicesPricingSectionLabels = Readonly<{
  offeringsTitle: string;
  offeringsViewAll: string;
  offeringsEditAriaLabel: string;
  offeringsEmpty: string;
  offeringsFilterAll: string;
  offeringsFilterAriaLabel: string;
  serviceConditionsTitle: string;
  serviceConditionsEditAriaLabel: string;
  serviceConditionsEmpty: string;
  skillsTitle: string;
  skillsViewAll: string;
  skillsEditAriaLabel: string;
  skillsEmpty: string;
  skillsValidationCount: (count: number) => string;
  offeringsFromLabel: string;
  offeringsQuoteRequiredLabel: string;
}>;

export type ProfileServicesPricingCapabilityRecords = Readonly<{
  skills: readonly Readonly<{
    id: string;
    skillKey: string;
    skillLevel?: string;
    validationStatus: string;
    confirmationCount?: number;
    iconKey?: string;
    featuredValidatorInitials?: readonly string[];
  }>[];
}>;

export type ProfileServicesPricingSectionProps = Readonly<{
  capabilityProjection?: ProfileServicesPricingCapabilityProjection;
  capabilityRecords: ProfileServicesPricingCapabilityRecords;
  className?: string;
  componentInstance?: string;
  labels: ProfileServicesPricingSectionLabels;
  onEditOfferings?: () => void;
  onEditServiceConditions?: () => void;
  onEditSkills?: () => void;
  onViewAllOfferings?: () => void;
  onViewAllSkills?: () => void;
  serviceConditionRecords: readonly ServiceConditionInput[];
  serviceRecords: readonly ServiceOfferingInput[];
}>;

function SectionEditIcon() {
  return (
    <svg className="profile-services-pricing-section__edit-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionChevronIcon() {
  return (
    <svg className="profile-services-pricing-section__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ServicesPricingSectionHeader({
  componentInstance,
  editAriaLabel,
  onEdit,
  onViewAll,
  title,
  viewAllLabel,
}: Readonly<{
  componentInstance: string;
  editAriaLabel?: string;
  onEdit?: () => void;
  onViewAll?: () => void;
  title: React.ReactNode;
  viewAllLabel?: string;
}>) {
  return (
    <div className="profile-section-header profile-services-pricing-section__header" data-component-instance={componentInstance}>
      <div className="profile-section-header__title-group">
        <h2>{title}</h2>
        {onEdit && editAriaLabel ? (
          <button
            type="button"
            className="profile-section-header__edit-button"
            aria-label={editAriaLabel}
            data-component-instance={`${componentInstance}EditButton`}
            onClick={onEdit}
          >
            <SectionEditIcon />
          </button>
        ) : null}
      </div>
      {onViewAll && viewAllLabel ? (
        <div className="profile-section-header__trailing">
          <button
            type="button"
            className="profile-section-header__view-all"
            data-component-instance={`${componentInstance}ViewAllButton`}
            onClick={onViewAll}
          >
            {viewAllLabel}
            <SectionChevronIcon />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function skillNameFromProjection(
  skillKey: string,
  projection?: ProfileServicesPricingCapabilityProjection,
): string {
  const match = projection?.skills.find(
    (entry) => entry.publicSkillKey.endsWith(`:${skillKey}`) || entry.publicSkillKey.includes(skillKey),
  );
  return match?.name ?? skillKey.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function ProfileServicesPricingSection({
  capabilityProjection,
  capabilityRecords,
  className,
  componentInstance = "profileServicesPricingSection",
  labels,
  onEditOfferings,
  onEditServiceConditions,
  onEditSkills,
  onViewAllOfferings,
  onViewAllSkills,
  serviceConditionRecords,
  serviceRecords,
}: ProfileServicesPricingSectionProps) {
  const [offeringFilter, setOfferingFilter] = useState(labels.offeringsFilterAll);

  const offerings = useMemo(
    () => buildServicesPricingOfferingDisplays(serviceRecords, {
      fromLabel: labels.offeringsFromLabel,
      quoteRequiredLabel: labels.offeringsQuoteRequiredLabel,
    }),
    [labels.offeringsFromLabel, labels.offeringsQuoteRequiredLabel, serviceRecords],
  );
  const conditions = useMemo(
    () => buildServicesPricingConditionDisplays(serviceConditionRecords),
    [serviceConditionRecords],
  );
  const skills = useMemo(() => {
    const skillInputs: SkillInput[] = capabilityRecords.skills.map((skill) => ({
      id: skill.id,
      skillKey: skill.skillKey,
      skillLevel: skill.skillLevel,
      validationStatus: skill.validationStatus,
      confirmationCount: skill.confirmationCount,
      iconKey: skill.iconKey,
      featuredValidatorInitials: skill.featuredValidatorInitials,
    }));
    return buildServicesPricingSkillDisplays(skillInputs).map((entry) => {
      const source = capabilityRecords.skills.find((skill) => skill.id === entry.id);
      return source
        ? { ...entry, name: skillNameFromProjection(source.skillKey, capabilityProjection) }
        : entry;
    });
  }, [capabilityProjection, capabilityRecords.skills]);
  const offeringFilters = useMemo(
    () => collectOfferingIndustryFilters(offerings, labels.offeringsFilterAll),
    [labels.offeringsFilterAll, offerings],
  );
  const filteredOfferings = useMemo(
    () => filterOfferingsByIndustry(offerings, offeringFilter, labels.offeringsFilterAll),
    [offeringFilter, labels.offeringsFilterAll, offerings],
  );

  return (
    <div
      className={["profile-services-pricing-grid", className].filter(Boolean).join(" ")}
      data-component-instance={componentInstance}
    >
      <div className="profile-services-pricing-grid__column">
        <section className="profile-card profile-services-pricing-card" data-component-instance={`${componentInstance}Offerings`}>
          <ServicesPricingSectionHeader
            componentInstance={`${componentInstance}OfferingsHeader`}
            editAriaLabel={labels.offeringsEditAriaLabel}
            onEdit={onEditOfferings}
            onViewAll={onViewAllOfferings}
            title={labels.offeringsTitle}
            viewAllLabel={labels.offeringsViewAll}
          />
          <ProfileFilterChipGroup
            className="profile-services-pricing-filters"
            ariaLabel={labels.offeringsFilterAriaLabel}
            activeValue={offeringFilter}
            onSelect={setOfferingFilter}
            allOption={{ value: labels.offeringsFilterAll, label: labels.offeringsFilterAll }}
            options={offeringFilters
              .filter((filter) => filter !== labels.offeringsFilterAll)
              .map((filter) => ({ value: filter, label: filter }))}
          />
          {filteredOfferings.length > 0 ? (
            <div className="profile-services-pricing-offerings">
              {filteredOfferings.map((offering) => (
                <article
                  key={offering.id}
                  className="profile-services-pricing-offering"
                  data-offering-tone={offering.visualTone}
                >
                  <span
                    className="profile-services-pricing-offering__icon"
                    style={{ backgroundColor: getServicesPricingOfferingIconPresentation(offering.iconKey).bg }}
                  >
                    <ServicesPricingOfferingIcon iconKey={offering.iconKey} />
                  </span>
                  <div className="profile-services-pricing-offering__body">
                    <strong>{offering.title}</strong>
                    {offering.tagLabel ? (
                      <span className="profile-services-pricing-offering__tag">{offering.tagLabel}</span>
                    ) : null}
                    {offering.description ? <p>{offering.description}</p> : null}
                  </div>
                  <div className="profile-services-pricing-offering__meta">
                    <strong>{offering.priceLabel}</strong>
                    {offering.turnaroundLabel ? <span>{offering.turnaroundLabel}</span> : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="profile-services-pricing-empty">{labels.offeringsEmpty}</p>
          )}
        </section>

      </div>

      <div className="profile-services-pricing-grid__column">
        <section className="profile-card profile-services-pricing-card" data-component-instance={`${componentInstance}Skills`}>
          <ServicesPricingSectionHeader
            componentInstance={`${componentInstance}SkillsHeader`}
            editAriaLabel={labels.skillsEditAriaLabel}
            onEdit={onEditSkills}
            onViewAll={onViewAllSkills}
            title={labels.skillsTitle}
            viewAllLabel={labels.skillsViewAll}
          />
          {skills.length > 0 ? (
            <div className="profile-services-pricing-skills">
              {skills.slice(0, 5).map((skill) => (
                <article key={skill.id} className="profile-services-pricing-skill" data-skill-tone={skill.visualTone}>
                  <div className="profile-services-pricing-skill__header">
                    <ServicesPricingSkillIcon
                      className="profile-services-pricing-skill__icon"
                      iconKey={skill.iconKey}
                    />
                    <div className="profile-services-pricing-skill__title-row">
                      <strong>{skill.name}</strong>
                      {skill.validatorInitials.length > 0 ? (
                        <span className="profile-services-pricing-skill__validators">
                          {skill.validatorInitials.slice(0, 5).map((initials) => (
                            <span key={`${skill.id}-${initials}`}>{initials}</span>
                          ))}
                        </span>
                      ) : null}
                    </div>
                    <span className="profile-services-pricing-skill__badge">
                      {labels.skillsValidationCount(skill.validationCount)}
                    </span>
                  </div>
                  <div className="profile-services-pricing-skill__progress" aria-hidden="true">
                    <span style={{ width: `${skill.validationPercent}%` }} />
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <p className="profile-services-pricing-empty">{labels.skillsEmpty}</p>
          )}
        </section>

        <section className="profile-card profile-services-pricing-card" data-component-instance={`${componentInstance}ServiceConditions`}>
          <ServicesPricingSectionHeader
            componentInstance={`${componentInstance}ServiceConditionsHeader`}
            editAriaLabel={labels.serviceConditionsEditAriaLabel}
            onEdit={onEditServiceConditions}
            title={labels.serviceConditionsTitle}
          />
          {conditions.length > 0 ? (
            <ul className="profile-services-pricing-conditions">
              {conditions.map((condition) => (
                <li key={condition.id}>
                  <strong>{condition.title}:</strong> {condition.description}
                </li>
              ))}
            </ul>
          ) : (
            <p className="profile-services-pricing-empty">{labels.serviceConditionsEmpty}</p>
          )}
        </section>
      </div>
    </div>
  );
}
